class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (this.querySelector('.cart-drawer__optimistic')) this.optimisticDismissed = true;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  beginOptimisticAdd(statusLabel) {
    this.optimisticWasActive = this.classList.contains('active');
    this.optimisticDismissed = false;

    if (!this.querySelector('.cart-drawer__optimistic')) {
      const optimisticState = document.createElement('div');
      optimisticState.className = 'cart-drawer__optimistic';
      optimisticState.setAttribute('role', 'status');
      optimisticState.setAttribute('aria-live', 'polite');
      optimisticState.innerHTML = `
        <span class="cart-drawer__optimistic-spinner" aria-hidden="true"></span>
        <span class="visually-hidden"></span>
      `;
      optimisticState.querySelector('.visually-hidden').textContent = statusLabel;
      this.querySelector('.drawer__inner')?.appendChild(optimisticState);
    }

    if (!this.classList.contains('active')) this.open();
  }

  cancelOptimisticAdd() {
    this.querySelector('.cart-drawer__optimistic')?.remove();

    const shouldClose = !this.optimisticWasActive && !this.optimisticDismissed;
    this.optimisticWasActive = undefined;
    this.optimisticDismissed = false;

    if (shouldClose) this.close();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.prepareDrawerForContents(parsedState);
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (sectionHTML !== null) sectionElement.innerHTML = sectionHTML;
    });

    this.finishDrawerRender();
  }

  renderContentsQuick(parsedState) {
    const wasEmpty = this.classList.contains('is-empty');
    this.prepareDrawerForContents(parsedState);
    this.updateCartIconBubble(parsedState.sections?.['cart-icon-bubble']);

    if (wasEmpty) {
      this.refreshDrawerContents({ replaceFullInner: true }).then(() => this.finishDrawerRender());
      return;
    }

    this.finishDrawerRender();
    this.refreshDrawerContents();
  }

  prepareDrawerForContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
  }

  updateCartIconBubble(sectionHTML) {
    if (!sectionHTML) return;

    const bubbleElement = document.getElementById('cart-icon-bubble');
    if (!bubbleElement) return;

    const bubbleHTML = this.getSectionInnerHTML(sectionHTML, '.shopify-section');
    if (bubbleHTML !== null) bubbleElement.innerHTML = bubbleHTML;
  }

  refreshDrawerContents({ replaceFullInner = false } = {}) {
    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.add('cart-drawer-items--refreshing');

    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (replaceFullInner) {
          const sourceInner = html.querySelector('.drawer__inner');
          const targetInner = this.querySelector('.drawer__inner');
          if (sourceInner && targetInner) {
            targetInner.innerHTML = sourceInner.innerHTML;
            return;
          }
        }

        this.querySelector('.drawer__inner-empty')?.remove();

        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = this.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.querySelector('cart-drawer-items')?.classList.remove('cart-drawer-items--refreshing');
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      });
  }

  finishDrawerRender() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

    const wasDismissed = this.optimisticDismissed;
    this.optimisticWasActive = undefined;
    this.optimisticDismissed = false;

    if (wasDismissed && !this.classList.contains('active')) return;

    if (this.classList.contains('active')) {
      document.body.classList.add('overflow-hidden');
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
      return;
    }

    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const target = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return target ? target.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
